import { FC, useState, useEffect } from 'react';
import { TaskCard } from '@/components/learning/TaskCard';
import {
  LEARNING_PATHS,
  calculateLevel,
  getNextLevelXP,
  getAccessiblePaths,
  updateTaskStatus,
  CONCEPTS
} from '@/utils/learningData';
import { UserProgress, LearningTask, Concept } from '@/types/learning';
import { learningApi } from '@/services/api';

export const LearningPage: FC = () => {
  const [progress, setProgress] = useState<UserProgress>({
    totalXP: 0,
    level: 1,
    completedTasks: [],
    achievements: [],
    lastActivityAt: new Date().toISOString()
  });
  const [loading, setLoading] = useState(true);
  const [selectedPathId, setSelectedPathId] = useState<string>('basics');
  const [selectedTask, setSelectedTask] = useState<LearningTask | null>(null);
  const [showTaskDetail, setShowTaskDetail] = useState(false);
  const [selectedConcept, setSelectedConcept] = useState<Concept | null>(null);

  // 加载用户进度
  useEffect(() => {
    const loadProgress = async () => {
      try {
        setLoading(true);
        const userProgress = await learningApi.getProgress();
        setProgress(userProgress);
      } catch (error) {
        console.error('Failed to load learning progress:', error);
        // 如果是404错误，说明用户是第一次访问，使用默认进度
        // 其他错误也使用默认进度
      } finally {
        setLoading(false);
      }
    };
    loadProgress();
  }, []);

  // 更新进度并同步到服务器
  const updateProgress = async (updates: Partial<UserProgress>) => {
    const newProgress = {
      ...progress,
      ...updates,
      lastActivityAt: new Date().toISOString()
    };
    setProgress(newProgress);

    // 异步保存到服务器
    try {
      await learningApi.updateProgress({
        total_xp: newProgress.totalXP,
        level: newProgress.level,
        completed_tasks: newProgress.completedTasks,
        current_task: newProgress.currentTask,
        achievements: newProgress.achievements
      });
    } catch (error) {
      console.error('Failed to save learning progress:', error);
      // 可以在这里添加用户提示
    }
  };

  // 获取可访问的学习路径
  const accessiblePaths = getAccessiblePaths(progress.totalXP);

  // 获取当前选中的路径（带更新后的任务状态）
  const currentPath = accessiblePaths.find(p => p.id === selectedPathId);
  const updatedPath = currentPath ? updateTaskStatus(currentPath, progress.completedTasks) : null;

  // 计算统计信息
  const totalTasks = LEARNING_PATHS.flatMap(p => p.tasks).length;
  const completedTasksCount = progress.completedTasks.length;
  const completionPercentage = Math.round((completedTasksCount / totalTasks) * 100);
  const currentLevel = calculateLevel(progress.totalXP);
  const nextLevelXP = getNextLevelXP(currentLevel);
  const xpProgress = ((progress.totalXP % (currentLevel ** 2 * 100)) / (nextLevelXP - (currentLevel - 1) ** 2 * 100)) * 100;

  // 开始任务
  const handleStartTask = (taskId: string) => {
    const task = updatedPath?.tasks.find(t => t.id === taskId);
    if (task && task.status !== 'locked') {
      setSelectedTask(task);
      setShowTaskDetail(true);
    }
  };

  // 完成任务
  const handleCompleteTask = (taskId: string) => {
    const task = updatedPath?.tasks.find(t => t.id === taskId);
    if (task && !progress.completedTasks.includes(taskId)) {
      updateProgress({
        totalXP: progress.totalXP + task.xpReward,
        level: calculateLevel(progress.totalXP + task.xpReward),
        completedTasks: [...progress.completedTasks, taskId]
      });
      setShowTaskDetail(false);
      setSelectedTask(null);
    }
  };

  // 查看概念详情
  const handleViewConcept = (conceptId: string) => {
    const concept = CONCEPTS[conceptId];
    if (concept) {
      setSelectedConcept(concept);
    }
  };

  // 加载中状态
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-16 h-16 border-4 border-primary-500 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-600">加载学习进度中...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {/* 头部Banner */}
      <div className="bg-gradient-to-r from-primary-600 to-purple-600 text-white">
        <div className="container mx-auto px-4 py-12">
          <div className="max-w-6xl mx-auto">
            <h1 className="text-4xl font-bold mb-4">🎓 AI编程训练营</h1>
            <p className="text-xl text-blue-100 mb-8">
              掌握AI辅助编程技能，开启高效开发之旅
            </p>

            {/* 用户进度卡片 */}
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                {/* 等级 */}
                <div>
                  <div className="text-sm text-blue-100 mb-1">当前等级</div>
                  <div className="text-3xl font-bold">Lv.{currentLevel}</div>
                  <div className="text-xs text-blue-100 mt-1">
                    {progress.totalXP} / {nextLevelXP} XP
                  </div>
                  <div className="mt-2 bg-white/20 rounded-full h-2">
                    <div
                      className="bg-yellow-400 h-2 rounded-full transition-all duration-500"
                      style={{ width: `${xpProgress}%` }}
                    />
                  </div>
                </div>

                {/* 已完成任务 */}
                <div>
                  <div className="text-sm text-blue-100 mb-1">已完成任务</div>
                  <div className="text-3xl font-bold">{completedTasksCount}</div>
                  <div className="text-xs text-blue-100 mt-1">共 {totalTasks} 个任务</div>
                </div>

                {/* 完成进度 */}
                <div>
                  <div className="text-sm text-blue-100 mb-1">总体进度</div>
                  <div className="text-3xl font-bold">{completionPercentage}%</div>
                  <div className="text-xs text-blue-100 mt-1">继续加油！</div>
                </div>

                {/* 解锁路径 */}
                <div>
                  <div className="text-sm text-blue-100 mb-1">已解锁路径</div>
                  <div className="text-3xl font-bold">{accessiblePaths.length}</div>
                  <div className="text-xs text-blue-100 mt-1">共 {LEARNING_PATHS.length} 条路径</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 主内容区 */}
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-6xl mx-auto">
          {/* 学习路径选择 */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">学习路径</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {LEARNING_PATHS.map((path) => {
                const isAccessible = accessiblePaths.some(p => p.id === path.id);
                const isSelected = selectedPathId === path.id;
                const pathTasks = path.tasks.length;
                const pathCompleted = path.tasks.filter(t =>
                  progress.completedTasks.includes(t.id)
                ).length;
                const pathProgress = Math.round((pathCompleted / pathTasks) * 100);

                return (
                  <button
                    key={path.id}
                    onClick={() => isAccessible && setSelectedPathId(path.id)}
                    disabled={!isAccessible}
                    className={`
                      p-4 rounded-lg text-left transition-all duration-200
                      ${isAccessible ? 'cursor-pointer hover:shadow-lg transform hover:scale-105' : 'opacity-50 cursor-not-allowed'}
                      ${isSelected ? 'bg-primary-600 text-white shadow-lg' : 'bg-white border-2 border-gray-200'}
                    `}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-3xl">{path.icon}</span>
                      {!isAccessible && <span className="text-2xl">🔒</span>}
                      {pathCompleted === pathTasks && <span className="text-2xl">✅</span>}
                    </div>
                    <h3 className={`font-semibold mb-1 ${isSelected ? 'text-white' : 'text-gray-900'}`}>
                      {path.title}
                    </h3>
                    <p className={`text-xs mb-3 ${isSelected ? 'text-blue-100' : 'text-gray-600'}`}>
                      {path.description}
                    </p>
                    <div className="space-y-1">
                      <div className="flex justify-between text-xs">
                        <span className={isSelected ? 'text-blue-100' : 'text-gray-500'}>
                          {pathCompleted} / {pathTasks} 任务
                        </span>
                        <span className={isSelected ? 'text-blue-100' : 'text-gray-500'}>
                          {pathProgress}%
                        </span>
                      </div>
                      <div className={`h-1.5 rounded-full ${isSelected ? 'bg-white/30' : 'bg-gray-200'}`}>
                        <div
                          className={`h-1.5 rounded-full transition-all duration-500 ${isSelected ? 'bg-white' : 'bg-primary-500'}`}
                          style={{ width: `${pathProgress}%` }}
                        />
                      </div>
                    </div>
                    {path.requiredXP && !isAccessible && (
                      <div className={`mt-2 text-xs ${isSelected ? 'text-blue-100' : 'text-gray-500'}`}>
                        需要 {path.requiredXP} XP 解锁
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* 任务列表 */}
          {updatedPath && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold text-gray-900">
                  {updatedPath.icon} {updatedPath.title}
                </h2>
                <div className="text-sm text-gray-600">
                  {updatedPath.tasks.filter(t => progress.completedTasks.includes(t.id)).length} / {updatedPath.tasks.length} 已完成
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {updatedPath.tasks.map((task) => (
                  <TaskCard
                    key={task.id}
                    task={task}
                    onStart={handleStartTask}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 任务详情弹窗 */}
      {showTaskDetail && selectedTask && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
              <h2 className="text-2xl font-bold text-gray-900">{selectedTask.title}</h2>
              <button
                onClick={() => setShowTaskDetail(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ×
              </button>
            </div>

            <div className="p-6">
              {/* 任务信息 */}
              <div className="mb-6">
                <p className="text-gray-700 mb-4">{selectedTask.description}</p>
                <div className="flex flex-wrap gap-2">
                  <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                    预计 {selectedTask.estimatedTime} 分钟
                  </span>
                  <span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm">
                    +{selectedTask.xpReward} XP
                  </span>
                </div>
              </div>

              {/* 学习目标 */}
              {selectedTask.content.objectives && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">📋 学习目标</h3>
                  <ul className="space-y-2">
                    {selectedTask.content.objectives.map((objective, index) => (
                      <li key={index} className="flex items-start">
                        <span className="text-primary-500 mr-2 mt-1">✓</span>
                        <span className="text-gray-700">{objective}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* 关键要点 */}
              {selectedTask.content.keyPoints && selectedTask.content.keyPoints.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">💡 关键要点</h3>
                  <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
                    <ul className="space-y-2">
                      {selectedTask.content.keyPoints.map((point, index) => (
                        <li key={index} className="flex items-start">
                          <span className="text-blue-600 mr-2">•</span>
                          <span className="text-gray-700">{point}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              {/* 相关概念 */}
              {selectedTask.concepts && selectedTask.concepts.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">📚 相关知识点</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {selectedTask.concepts.map((conceptId) => {
                      const concept = CONCEPTS[conceptId];
                      return concept ? (
                        <button
                          key={conceptId}
                          onClick={() => handleViewConcept(conceptId)}
                          className="text-left p-3 bg-white border-2 border-gray-200 rounded-lg hover:border-primary-500 hover:shadow transition-all"
                        >
                          <div className="font-medium text-gray-900 mb-1">{concept.title}</div>
                          <div className="text-sm text-gray-600">{concept.description}</div>
                        </button>
                      ) : null;
                    })}
                  </div>
                </div>
              )}

              {/* 练习题 */}
              {selectedTask.content.exercises && selectedTask.content.exercises.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">✏️ 实践练习</h3>
                  {selectedTask.content.exercises.map((exercise, index) => (
                    <div key={index} className="mb-4 p-4 bg-gray-50 rounded-lg">
                      <div className="font-medium text-gray-900 mb-2">
                        练习 {index + 1}:
                      </div>
                      <div className="text-gray-700 whitespace-pre-wrap mb-3">
                        {exercise.question}
                      </div>
                      {exercise.hints && exercise.hints.length > 0 && (
                        <details className="mb-2">
                          <summary className="cursor-pointer text-blue-600 hover:text-blue-800">
                            💡 查看提示
                          </summary>
                          <ul className="mt-2 ml-4 space-y-1">
                            {exercise.hints.map((hint, hintIndex) => (
                              <li key={hintIndex} className="text-sm text-gray-600">
                                • {hint}
                              </li>
                            ))}
                          </ul>
                        </details>
                      )}
                      <details>
                        <summary className="cursor-pointer text-green-600 hover:text-green-800">
                          ✓ 查看参考答案
                        </summary>
                        <div className="mt-2 p-3 bg-green-50 rounded text-sm text-gray-700 whitespace-pre-wrap">
                          {exercise.answer}
                        </div>
                      </details>
                    </div>
                  ))}
                </div>
              )}

              {/* 操作按钮 */}
              <div className="flex gap-3">
                <button
                  onClick={() => handleCompleteTask(selectedTask.id)}
                  disabled={progress.completedTasks.includes(selectedTask.id)}
                  className={`
                    flex-1 py-3 px-6 rounded-lg font-medium
                    ${progress.completedTasks.includes(selectedTask.id)
                      ? 'bg-green-100 text-green-800 cursor-default'
                      : 'bg-primary-600 hover:bg-primary-700 text-white'
                    }
                    transition-colors
                  `}
                >
                  {progress.completedTasks.includes(selectedTask.id) ? '✓ 已完成' : '标记为已完成'}
                </button>
                <button
                  onClick={() => setShowTaskDetail(false)}
                  className="px-6 py-3 border-2 border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  关闭
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 概念详情弹窗 */}
      {selectedConcept && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
              <h2 className="text-2xl font-bold text-gray-900">{selectedConcept.title}</h2>
              <button
                onClick={() => setSelectedConcept(null)}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ×
              </button>
            </div>

            <div className="p-6">
              <p className="text-lg text-gray-700 mb-6">{selectedConcept.description}</p>

              <div className="prose prose-blue max-w-none">
                <div className="whitespace-pre-wrap text-gray-700">
                  {selectedConcept.content}
                </div>
              </div>

              {selectedConcept.examples && selectedConcept.examples.length > 0 && (
                <div className="mt-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">📝 示例</h3>
                  {selectedConcept.examples.map((example, index) => (
                    <div key={index} className="mb-3 p-4 bg-gray-50 rounded-lg">
                      <pre className="text-sm text-gray-700 whitespace-pre-wrap font-mono">
                        {example}
                      </pre>
                    </div>
                  ))}
                </div>
              )}

              {selectedConcept.relatedConcepts && selectedConcept.relatedConcepts.length > 0 && (
                <div className="mt-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">🔗 相关概念</h3>
                  <div className="flex flex-wrap gap-2">
                    {selectedConcept.relatedConcepts.map((relatedId) => {
                      const related = CONCEPTS[relatedId];
                      return related ? (
                        <button
                          key={relatedId}
                          onClick={() => setSelectedConcept(related)}
                          className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm hover:bg-blue-200 transition-colors"
                        >
                          {related.title}
                        </button>
                      ) : null;
                    })}
                  </div>
                </div>
              )}

              <div className="mt-6">
                <button
                  onClick={() => setSelectedConcept(null)}
                  className="w-full py-3 px-6 border-2 border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  关闭
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
